// 📄 pages/Proyectos/components/PermisosMiembroModal.tsx
// Panel de "qué ve/puede hacer este miembro" — checkboxes de visibilidad
// de sidebar (5 categorías de menuConfig.tsx) +, si quien abre el modal
// es el dueño del proyecto, un toggle para delegarle a este miembro la
// facultad de gestionar los permisos de otros (ver
// puedeGestionarPermisosDeProyecto en firestore.rules).
//
// Incidentes/Miembros/Editar Proyecto tienen protección REAL en
// firestore.rules (no solo UI) — el resto (Soluciones/Configuración/
// Herramientas) es únicamente visual, Firestore no puede ocultar campo
// por campo dentro del mismo doc de proyecto. Ver MemberPermisos en
// projectService.ts.
//
// "Detalle del Proyecto", "Mis Horas" y "Mi Espacio" NUNCA aparecen acá —
// son personales/básicos, siempre visibles, no configurables (pedido
// explícito del usuario).
import React, { useEffect, useState } from 'react';
import { X, ShieldCheck, RefreshCw, KeyRound } from 'lucide-react';
import {
  funcionalidadesItems,
  HERRAMIENTAS_ITEMS,
  CONFIGURACION_ITEMS,
  segunModulos,
  PROYECTO_ITEMS
} from '../../Dashboard/menuConfig.tsx';
import { SOLUCIONES, OTRAS_SOLUCIONES } from '../solucionesConfig.ts';
import { ProjectMember, Project, getMemberPermisos, updateMemberPermissions, setPuedeGestionarPermisos } from '../../../services/projectService.ts';
import { listarPermisosCredenciales, guardarPermisoCredenciales } from '../../../services/credencialAccesoService.ts';
import { esProyectoSap } from '../modulosConfig.ts';

// Solo estos 2 ítems de "Proyecto" son configurables — Detalle/Horas/Mi
// Espacio quedan afuera a propósito (ver comentario de archivo).
const PROYECTO_ITEMS_CONFIGURABLES = PROYECTO_ITEMS.filter((i) => i.id === 'incidentes' || i.id === 'mesa-ayuda' || i.id === 'miembros');

interface PermisosMiembroModalProps {
  projectId: string;
  project: Project;
  miembro: ProjectMember;
  // Solo el dueño del proyecto ve el toggle de delegación — ver
  // firestore.rules, únicamente él puede tocar ese campo.
  esCreador: boolean;
  // Owner/admin de la org que no gestiona el sidebar: solo ve y guarda
  // "Puede obtener credenciales" (las reglas no le dejan tocar permisos/{uid}).
  soloCredenciales?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function PermisosMiembroModal({ projectId, project, miembro, esCreador, soloCredenciales = false, onClose, onSaved }: PermisosMiembroModalProps) {
  const [loading, setLoading] = useState(true);
  const [seccionesOcultas, setSeccionesOcultas] = useState<string[]>([]);
  const [solucionesOcultas, setSolucionesOcultas] = useState<string[]>([]);
  const [puedeGestionarPermisos, setPuedeGestionarPermisosState] = useState(false);
  // "Obtener credenciales" (docs/plans/plan-revelado-credenciales.md): vive
  // en el backend, no en permisos/{uid}. null = quien abre el modal no lo
  // gestiona (solo el creador del proyecto u owner/admin de la org; lo decide
  // el backend con 403).
  const [puedeObtenerCredenciales, setPuedeObtenerCredenciales] = useState<boolean | null>(null);
  const [puedeObtenerOriginal, setPuedeObtenerOriginal] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    Promise.all([
      // Un owner/admin de la org que no es miembro del proyecto no puede leer permisos/{uid}.
      soloCredenciales ? Promise.resolve(null) : getMemberPermisos(projectId, miembro.userId),
      listarPermisosCredenciales(projectId)
        .then((miembros) => miembros.some((m) => m.uid === miembro.userId))
        .catch(() => null)
    ])
      .then(([permisos, obtener]) => {
        if (cancelado) return;
        setSeccionesOcultas(permisos?.seccionesOcultas || []);
        setSolucionesOcultas(permisos?.solucionesOcultas || []);
        setPuedeGestionarPermisosState(permisos?.puedeGestionarPermisos === true);
        setPuedeObtenerCredenciales(obtener);
        setPuedeObtenerOriginal(obtener);
      })
      .catch((err) => { if (!cancelado) setError(err.message || 'Error cargando los permisos actuales.'); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [projectId, miembro.userId, soloCredenciales]);

  const toggle = (lista: string[], setLista: (v: string[]) => void, id: string) => {
    setLista(lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id]);
  };

  const solucionesActivas = project.solucionesActivas || ['commerce'];
  const sap = esProyectoSap(project);
  const solucionesDelProyecto = [
    ...(solucionesActivas.includes('commerce') ? [{ slug: 'commerce', label: SOLUCIONES.commerce.label }] : []),
    ...OTRAS_SOLUCIONES.filter((s) => solucionesActivas.includes(s.slug)).map((s) => ({ slug: s.slug, label: s.label }))
  ];

  const handleGuardar = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!soloCredenciales) await updateMemberPermissions(projectId, miembro.userId, seccionesOcultas, solucionesOcultas);
      if (esCreador) {
        await setPuedeGestionarPermisos(projectId, miembro.userId, puedeGestionarPermisos);
      }
      if (puedeObtenerCredenciales !== null && puedeObtenerCredenciales !== puedeObtenerOriginal) {
        await guardarPermisoCredenciales(projectId, miembro.userId, puedeObtenerCredenciales);
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar los permisos.');
    } finally {
      setSaving(false);
    }
  };

  const Grupo = ({ titulo, items, lista, setLista, getId, getLabel }: {
    titulo: string;
    items: any[];
    lista: string[];
    setLista: (v: string[]) => void;
    getId: (item: any) => string;
    getLabel: (item: any) => string;
  }) => {
    if (items.length === 0) return null;
    return (
      <div>
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">{titulo}</p>
        <div className="space-y-1.5">
          {items.map((item) => {
            const id = getId(item);
            const visible = !lista.includes(id);
            return (
              <label key={id} className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer hover:text-white transition-colors">
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={() => toggle(lista, setLista, id)}
                  className="accent-indigo-500 w-4 h-4"
                />
                {getLabel(item)}
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <div>
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-400" /> Permisos de {miembro.email}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Qué secciones ve en el sidebar de este proyecto.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
            <p className="text-sm">Cargando permisos actuales...</p>
          </div>
        ) : (
          <>
            <div className="p-5 space-y-5 overflow-y-auto">
              {!soloCredenciales && <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-3 text-xs text-amber-200/80">
                Incidentes, Miembros y Editar Proyecto quedan realmente bloqueados si los ocultás acá. El resto (Soluciones/Configuración/Herramientas) solo oculta el botón del sidebar — Firestore no puede restringir esos datos campo por campo.
              </div>}

              {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

              {esCreador && miembro.userId !== project.creadoPor && (
                <label className="flex items-start gap-2.5 text-sm text-gray-200 cursor-pointer bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-3">
                  <input
                    type="checkbox"
                    checked={puedeGestionarPermisos}
                    onChange={(e) => setPuedeGestionarPermisosState(e.target.checked)}
                    className="accent-indigo-500 w-4 h-4 mt-0.5"
                  />
                  <span>
                    <span className="font-medium">Puede gestionar permisos de otros miembros</span>
                    <span className="block text-xs text-gray-500 mt-0.5">Le da acceso al botón "Permisos" de cualquier miembro, igual que vos. Solo vos (el dueño del proyecto) podés activar o desactivar esto.</span>
                  </span>
                </label>
              )}

              {puedeObtenerCredenciales !== null && (
                <label className="flex items-start gap-2.5 text-sm text-gray-200 cursor-pointer bg-blue-500/5 border border-blue-500/20 rounded-xl p-3">
                  <input
                    type="checkbox"
                    checked={puedeObtenerCredenciales}
                    onChange={(e) => setPuedeObtenerCredenciales(e.target.checked)}
                    className="accent-blue-500 w-4 h-4 mt-0.5"
                  />
                  <span>
                    <span className="font-medium flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-blue-400" /> Puede obtener credenciales</span>
                    <span className="block text-xs text-gray-500 mt-0.5">Puede pedir ver contraseñas guardadas (PUser de CIG, SUNAT, APIs, Vault). Cada pedido necesita aprobación y queda en el historial. Solo el creador del proyecto o el owner/admin de la organización cambian esto.</span>
                  </span>
                </label>
              )}

              {soloCredenciales && puedeObtenerCredenciales === null && (
                <p className="text-sm text-gray-500">No tienes permisos que gestionar para este miembro.</p>
              )}

              {!soloCredenciales && <>
              <Grupo
                titulo="Soluciones del proyecto"
                items={sap ? solucionesDelProyecto : []}
                lista={solucionesOcultas}
                setLista={setSolucionesOcultas}
                getId={(s) => s.slug}
                getLabel={(s) => s.label}
              />
              <Grupo
                titulo="Funciones"
                items={segunModulos(funcionalidadesItems(true, solucionesActivas.includes('slp'), solucionesActivas.includes('commerce')), sap)}
                lista={seccionesOcultas}
                setLista={setSeccionesOcultas}
                getId={(i) => i.id}
                getLabel={(i) => i.label}
              />
              <Grupo
                titulo="Herramientas"
                items={segunModulos(HERRAMIENTAS_ITEMS, sap)}
                lista={seccionesOcultas}
                setLista={setSeccionesOcultas}
                getId={(i) => i.id}
                getLabel={(i) => i.label}
              />
              <Grupo
                titulo="Proyecto"
                items={PROYECTO_ITEMS_CONFIGURABLES}
                lista={seccionesOcultas}
                setLista={setSeccionesOcultas}
                getId={(i) => i.id}
                getLabel={(i) => i.label}
              />
              <Grupo
                titulo="Configuración"
                items={segunModulos(CONFIGURACION_ITEMS, sap)}
                lista={seccionesOcultas}
                setLista={setSeccionesOcultas}
                getId={(i) => i.id}
                getLabel={(i) => i.label}
              />

              <p className="text-[11px] text-gray-600">Detalle del Proyecto, Mis Horas y Mi Espacio siempre están visibles.</p>
              </>}
            </div>

            <div className="flex justify-end gap-2 p-5 border-t border-white/10 flex-shrink-0">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleGuardar}
                disabled={saving}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Guardar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
